import { calculateSessionNodes } from '@/modules/tryIt/utils/utils';
import {
  CollectionMessage,
  NodeType,
  SynthesisResponseCode,
  TryItCollection,
  UserMessage,
} from '@/shared/types/tryIt';

// Re-export from new location so legacy callers keep compiling while the
// new modal owns the canonical implementation.
export { calculateSessionNodes };

/**
 * Removes duplicate datasources based on unique combinations of document_id and datasource_id.
 *
 * @function
 * @param {Array.<Object>} datasources - An array of datasources to filter for uniqueness.
 * @param {string} datasources[].document_id - The ID of the document.
 * @param {string} datasources[].datasource_id - The ID of the datasource.
 * @returns {Array.<Object>} An array of unique datasources based on document_id and datasource_id combination.
 */
export const removeDuplicateDocuments = (
  fragments: TryItCollection['fragments']
): TryItCollection['fragments'] => {
  if (!fragments) {
    return [];
  }

  const seenPairs = new Set();
  const uniqueEntries = [];

  for (const entry of fragments) {
    const pair = `${entry.document_id}-${entry.datasource_id}`;
    if (!seenPairs.has(pair)) {
      seenPairs.add(pair);
      uniqueEntries.push(entry);
    }
  }
  return uniqueEntries;
};

/**
 * Groups an array of objects by a given key within each object.
 * If the key does not exist on an item, that item will not be included in the output.
 *
 * @template T - The type of the elements in the input array.
 *
 * @param {T[]} array - The array to group.
 * @param {string} key - The key of the property to group by.
 *
 * @returns {T[][]} - An array of arrays, where each subarray contains elements
 * that are grouped by the specified key.
 */
export const groupBy = <T>(array: T[], key: string): T[][] => {
  if (!array) {
    return [];
  }

  const grouped = array.reduce((acc, item) => {
    if (!acc[item[key]]) {
      acc[item[key]] = [];
    }
    acc[item[key]].push(item);
    return acc;
  }, {});

  return Object.values(grouped);
};

/**
 * Extracts all node details needed by detail section components.
 * Consolidates intent extraction, planner info, user message, and amendment status.
 *
 * Intents can be found in multiple locations depending on the data source:
 * 1. node.intents - set during live Try-It sessions (via updateNodes in helper.ts)
 * 2. node.messages[0].intents - set during log replay (via calculateSessionNodes in helper.ts)
 * Note: messages[index] is NOT used because the filtered messages array has different indices
 * than the nodes array, which would cause wrong confidence values to be displayed (MOV-10672).
 */
export const getNodeDetailsForSection = (node: NodeType | Partial<NodeType> | undefined) => {
  // Extract intents from node or messages
  const intents = node?.intents || (node?.messages?.[0] as UserMessage)?.intents;
  const intent = intents?.[0];

  // Extract user message text
  const userMessage = (node?.messages?.[0] as CollectionMessage)?.text;

  // Calculate planner intent info
  const plannerIntents = node?.planner_intents;
  const isPlannerIntentOfftopic =
    !!plannerIntents?.includes('offtopics') && plannerIntents?.length === 1;
  const plannerIntent = !isPlannerIntentOfftopic ? plannerIntents?.[0] : undefined;

  // Check amendment status
  const isStandaloneAmended = !!node?.corrections?.find((c) => c.task === 'standalone');
  const isIntentAmended = !!node?.corrections?.find(
    (c) => c.task === 'planner' && intents?.some((i) => i.intent === c.correction)
  );

  return {
    intent,
    intents,
    userMessage,
    plannerIntents,
    isPlannerIntentOfftopic,
    plannerIntent,
    isStandaloneAmended,
    isIntentAmended,
    showAddTrainingPhrase: intent?.confidence !== 1,
  };
};

export const DEPRECATED_CUSTOM_INSTRUCTIONS = 'Custom Instructions';
export const DEPRECATED_LIVE_INSTRUCTIONS = 'Live Instructions';
export const CUSTOM_INSTRUCTIONS = 'Instructions';
export const UNIFIED_GUIDELINES = 'Guidelines';
export const LIVE_INSTRUCTIONS = 'User Info';
export const DIALOG_HISTORY = 'Dialog History';

const isInstructions = (collection: TryItCollection) =>
  collection?.response_code === SynthesisResponseCode.OK &&
  !!collection?.external_urls?.find((url) =>
    [
      DEPRECATED_CUSTOM_INSTRUCTIONS,
      DEPRECATED_LIVE_INSTRUCTIONS,
      LIVE_INSTRUCTIONS,
      CUSTOM_INSTRUCTIONS,
      UNIFIED_GUIDELINES,
      DIALOG_HISTORY,
    ].includes(url.name)
  );

/**
 *Check if the collection schema is sources from live/custom instructions
 * or has empty sources but went through LLM
 */
export const isMessageFromKnowledge = (collection: TryItCollection) => {
  return (
    isInstructions(collection) ||
    (collection?.response_code === SynthesisResponseCode.OK &&
      (!collection?.external_urls || collection?.external_urls?.length == 0) &&
      (!collection?.fragments || collection?.fragments?.length == 0))
  );
};

/**
 * Checks if the provided schema has any external URLs
 * with at least one associated document ID. If there are no external_urls,
 * it checks if there are fragments present and the response code is 0.
 */

export const isMessageFromCollection = (collection: TryItCollection) => {
  const foundDocuments = collection?.external_urls?.filter((url) => !!url.document_id);
  if (collection?.external_urls?.length > 0 && foundDocuments?.length > 0) {
    return true;
  }

  // Consider it from collection if we have fragments AND either:
  // 1. response_code is OK (0), OR
  // 2. response_code is not set (null/undefined) - happens when TryIt backend doesn't populate it
  return (
    collection?.fragments?.length > 0 &&
    (collection?.response_code === SynthesisResponseCode.OK || collection?.response_code == null)
  );
};
